import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface AudioFile {
  id: string;
  title: string;
  original_filename: string;
  file_url: string;
  object_name: string;
  mime_type: string;
  file_size: number;
  tags: string[];
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

export interface AudioListResponse {
  items: AudioFile[];
  total: number;
  skip: number;
  limit: number;
}

export interface AudioUpdate {
  title?: string;
  tags?: string[];
}

export interface BatchUploadResult {
  filename: string;
  success: boolean;
  audio?: AudioFile;
  error?: string;
}

export interface BatchDeleteRequest {
  ids: string[];
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class AudioService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/audio`;

  /** GET /api/v1/audio/ */
  list(skip = 0, limit = 50, search?: string): Observable<AudioListResponse> {
    const params: Record<string, string | number> = { skip, limit };
    if (search?.trim()) params['search'] = search.trim();
    return this.http.get<AudioListResponse>(`${this.apiUrl}/`, { params });
  }

  /** POST /api/v1/audio/upload */
  uploadSingle(file: File): Observable<AudioFile> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<AudioFile>(`${this.apiUrl}/upload`, form);
  }

  /** POST /api/v1/audio/upload/batch */
  uploadBatch(files: File[]): Observable<BatchUploadResult[]> {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    return this.http.post<BatchUploadResult[]>(`${this.apiUrl}/upload/batch`, form);
  }

  /** PATCH /api/v1/audio/{id} */
  update(id: string, payload: AudioUpdate): Observable<AudioFile> {
    return this.http.patch<AudioFile>(`${this.apiUrl}/${id}`, payload);
  }

  /** DELETE /api/v1/audio/{id} */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /** DELETE /api/v1/audio/ (batch) */
  batchDelete(ids: string[]): Observable<{ deleted: number }> {
    return this.http.delete<{ deleted: number }>(`${this.apiUrl}/`, { body: { ids } });
  }
}
